import { FC, useState, useEffect, createContext, useContext, Dispatch, SetStateAction } from 'react';
import { LayoutSplashScreen } from '../../../../_myb/layout/core';
import { AuthModel, UserModel } from './_models';
import * as authHelper from './AuthHelpers';
import { getUserByToken } from './_requests';
import { WithChildren } from '../../../../_myb/helpers';
import { AppProvider, globalValue, useAppContext } from '../../apps/AppContext';
import { cacheBizLogo, fetchBizMaster, fetchBizSettings, getUserRoleAccess, getSubscriptionDetails } from '../../../api';
import { getCurrentLang, setLanguage } from '../../../../_myb/i18n/LangConfig';
import { toast } from 'react-toastify';
import { updateTidioUser } from '../../apps/chat/TidioChat';
import { brandSettings } from '../../settings/BrandSettings';

type AuthContextProps = {
  auth: AuthModel | undefined;
  saveAuth: (auth: AuthModel | undefined) => void;
  currentUser: UserModel | undefined;
  setCurrentUser: Dispatch<SetStateAction<UserModel | undefined>>;
  switchToken: () => Promise<void>; // Make switchToken async
  saveCurrentUser: (user: UserModel | undefined) => Promise<void>;
  triggerAuthCheck: () => void;
  logout: () => void;
};

export let logoutFunction = (isLoginExpired?: boolean) => {};

export const setLogoutFunction = (logout: (isLoginExpired?: boolean) => void) => {
  logoutFunction = logout;
};

const initAuthContextPropsState = {
  auth: authHelper.getAuth(),
  saveAuth: () => {},
  currentUser: undefined,
  setCurrentUser: () => {},
  saveCurrentUser: async () => {},
  triggerAuthCheck: () => {},
  switchToken:async ()=>{},
  logout: () => {},
};

const AuthContext = createContext<AuthContextProps>(initAuthContextPropsState);

const useAuth = () => {
  return useContext(AuthContext);
};

export const triggerExternalLogout = () => {
  const { logout } = useAuth();
  logout();
};


const AuthProvider: FC<WithChildren> = ({ children }) => {
  const [auth, setAuth] = useState<AuthModel | undefined>(authHelper.getAuth());
  const { setBizMaster, resetContext, setBizSettings, setRoleAccess, setSubscriptionData } = useAppContext();
  const [forceLogout,setForceLogout] = useState(false)
  const [currentUser, setCurrentUser] = useState<UserModel | undefined>();
 
  useEffect(() => {
    const handleWindowFocus = () => {
      if (document.visibilityState === 'visible') {
        triggerAuthCheck(); // Check token expiration or perform other actions
      }
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        triggerAuthCheck(); // Check token expiration on resume
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleWindowFocus);
    setLogoutFunction(logout)

    // Hourly auth check interval
    const intervalId = setInterval(() => {
      triggerAuthCheck();
    }, 30 * 60 * 1000); // 30 mins in milliseconds

    return () => {
      clearInterval(intervalId);
      window.removeEventListener('focus', handleWindowFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange)};
  }, []);
  
  const triggerAuthCheck = () => {
    const apiToken = auth?.api_token || authHelper.getAuth()?.api_token
    if (apiToken && authHelper.isTokenExpired(apiToken)) {
      console.warn('[AUTH] triggerAuthCheck: token EXPIRED — setting forceLogout');
      setForceLogout(true)
    }
  };

  const updateUser = async() =>{
   
    if(auth?.api_token){
      const user = await  getUserByToken(auth?.api_token)
      
      await saveCurrentUser(user)
    }
  }

  useEffect(() => {
    updateUser()
  },[auth?.api_token])

  useEffect(()=>{
    if(forceLogout) logout(true)
  },[forceLogout])

  const saveCurrentUser = async (user: UserModel | undefined) => {
    const [{ bizMaster, logo }, bizSettings, roleAccessData, subscriptionData] = await Promise.all([
      fetchBizMaster(),
      fetchBizSettings(),
      getUserRoleAccess().catch(() => undefined),
      getSubscriptionDetails().catch(() => undefined),
    ]);
    if(brandSettings.inventorySetting === false) bizSettings.inventoryFlag = brandSettings.inventorySetting
    globalValue.bizMaster = bizMaster;
    await cacheBizLogo(logo);
    setBizMaster(bizMaster);
    setBizSettings(bizSettings);
    if (roleAccessData) setRoleAccess(roleAccessData);
    if (subscriptionData) {
      setSubscriptionData(subscriptionData);
      globalValue.subscriptionData = subscriptionData;
    }
    // Language is a per-user preference (User.language on the backend), not a
    // business-wide setting — bizSettings.language only remains as a fallback
    // default for accounts predating the per-user field.
    const preferredLang = user?.language || bizSettings.language;
    if (preferredLang && preferredLang !== getCurrentLang()) {
      setLanguage(preferredLang);
      return;
    }
    setCurrentUser(user);
  };

  const saveAuth = (auth: AuthModel | undefined) => {
    if (auth) {
      authHelper.clearLoggingOut(); // Reset block so subsequent API calls go through
      authHelper.setAuth(auth);
      setAuth(auth)
    } else {
      authHelper.removeAuth();
      setAuth(undefined)
    }
  };

  const logout = (isLoginExpired?:boolean) => {
    if(!currentUser) {
      return //Already Logged Out
    }
    authHelper.markLoggingOut(); // Block all in-flight and pending API calls immediately
    console.warn(`[AUTH] logout called — isLoginExpired=${isLoginExpired}, currentPath=${window.location.pathname}`);
    const logoutMsg = (isLoginExpired===true) ? 'Login expired!' : 'Logging out'
    toast.info(logoutMsg)
    resetContext();
    authHelper.clearCachedData();
    saveAuth(undefined);
    setCurrentUser(undefined);
  };

  const switchToken = async () => {
    
    resetContext();
    authHelper.clearCachedData();
  }

  return (
    <AuthContext.Provider value={{ auth, saveAuth, currentUser, setCurrentUser, saveCurrentUser, switchToken, triggerAuthCheck, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

const AuthInit: FC<WithChildren> = ({ children }) => {
  const { auth, currentUser, logout, saveCurrentUser } = useAuth();
  const [showSplashScreen, setShowSplashScreen] = useState(true);
  const [toggleAuthCheck] = useState(true);

  const requestUser = async (apiToken: string) => {
    try {
      if (!currentUser) {
        const user = await getUserByToken(apiToken);
        if (user) {
          authHelper.updateAxiosToken(apiToken);
          await saveCurrentUser(user);
          updateTidioUser({
            distinct_id: globalValue.bizMaster?.bizId,
            user_id: user.userId,
            name: globalValue.bizMaster?.bizDetails?.bizName,
          });
        }
      }
    } catch (error) {
      if (currentUser) {
        logout();
      }
    } finally {
      setShowSplashScreen(false);
    }
  };

  // Check token expiry on app resume or initial load
  useEffect(() => {
    if (auth && auth.api_token) {
      if (authHelper.isTokenExpired(auth.api_token)) {
        logout();
        setShowSplashScreen(false);
      } else {
        requestUser(auth.api_token);
      }
    } else {
      logout();
      setShowSplashScreen(false);
    }
    // eslint-disable-next-line
  }, [toggleAuthCheck]);

  return showSplashScreen ? <LayoutSplashScreen /> : <>{children}</>;
};

export { AuthProvider, AuthInit, useAuth };
